/**
 * 권한 선택 트리 — 도메인 › 탭 › 세부 권한 3계층.
 *
 * 왜 이 구조인가
 *   권한 코드는 55개고 대부분 "조회/관리" 쌍이라, 평면 체크박스 목록으로 두면 스크롤만 길고
 *   실제로는 탭 단위로 한꺼번에 주게 된다("재무 자금일보 담당자에게 자금일보 권한"). 그래서
 *   기본 조작 단위를 <b>탭</b>으로 올리고, 세부 권한은 사이드바처럼 화살표로 펼쳐서 본다.
 *   도메인·탭 체크박스는 3-state(전체/일부/없음) — 일부만 켜져 있으면 indeterminate.
 *
 *   도메인/탭 이름은 백엔드 PermissionCode 의 domain·tab 필드가 유일한 출처다.
 *   여기서 다시 분류표를 만들지 않는다(만들면 코드가 추가될 때마다 두 군데를 고쳐야 한다).
 *
 * 단일 권한 탭 처리
 *   탭에 권한이 1개뿐이면(예: 결재 › 관리자 모드) 탭 행과 권한 행이 같은 말이 된다.
 *   이 경우 계층을 한 단 접어서 탭 행 자체를 권한 행으로 렌더한다 — 의미 없는 화살표를 없앤다.
 *
 * props
 *   perms       : [{code, nameKr, description, domain, tab}] — /api/permissions/list 응답
 *   selected    : Set<code> — 현재 체크된 코드
 *   onChange    : (nextSet) => void — 체크 변경 시 새 Set 을 그대로 넘긴다
 *   locks       : Map<code, reason> — 편집 불가 코드와 그 사유(툴팁). 부서 자동권한·관리자 전용 코드 등
 *   emptyText   : 목록이 비었을 때 문구
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Checkbox, Collapse, Divider, Tooltip, Typography } from "@mui/material";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { buildPermissionTree } from "./permissionGrouping";
import SubTreeLine from "../common/SubTreeLine";
import { checkboxSx, DOMAIN_ROW_PL, EXPAND_ICON, ROW_PR, TREE_INSET } from "./treeLayout";

/** 펼침 화살표 — 행 오른쪽 끝. 사이드바와 같은 모양(위/아래 꺾쇠). */
function Expander({ open }) {
    const Icon = open ? ExpandLessRoundedIcon : ExpandMoreRoundedIcon;
    return <Icon sx={{ fontSize: EXPAND_ICON, color: "#94a3b8", flexShrink: 0 }} />;
}

/** 묶음(도메인·탭) 전체가 잠겼음을 알리는 표시 — 개별 권한 행의 🔒 와 같은 의미. */
function LockedHint() {
    return (
        <Tooltip title="편집할 수 없는 권한만 있습니다 (부서 자동권한 또는 관리자 전용)" placement="top" arrow>
            <LockOutlinedIcon sx={{ fontSize: 13, color: "#94a3b8", flexShrink: 0 }} />
        </Tooltip>
    );
}

/** n/m 카운트 배지 */
function Count({ on, total }) {
    return (
        <Typography sx={{
            fontSize: 11, fontWeight: 700, flexShrink: 0,
            color: on === 0 ? "#cbd5e1" : "#475569",
        }}>
            {on}/{total}
        </Typography>
    );
}

export default function PermissionTree({
    perms,
    selected,
    onChange,
    locks,
    emptyText = "권한 목록을 불러오지 못했습니다.",
}) {
    const tree = useMemo(() => buildPermissionTree(perms, { skipLegacy: true }), [perms]);
    const lockOf = (code) => locks?.get?.(code);

    const [openDomains, setOpenDomains] = useState(new Set());
    const [openTabs, setOpenTabs] = useState(new Set());

    /**
     * 이미 권한이 있는 도메인은 펼친 채로 시작 — 뭘 갖고 있는지 바로 보이게.
     *
     * <p>useState 초기값으로 못 하는 이유: perms·selected 는 모달이 열린 뒤 API 로 채워지므로
     * 첫 렌더 시점엔 대개 비어 있다. 목록이 처음 들어오는 순간에 한 번만 seed 하고, 그 뒤에는
     * 사용자가 접은 걸 다시 펼치지 않도록 건드리지 않는다.
     */
    const seeded = useRef(false);
    useEffect(() => {
        if (seeded.current || tree.length === 0) return;
        seeded.current = true;
        const init = new Set();
        for (const d of tree) {
            if (d.tabs.some(t => t.perms.some(p => selected?.has(p.code)))) init.add(d.domain);
        }
        setOpenDomains(init);
        // selected 는 seed 시점 값만 쓴다 — 체크할 때마다 다시 펼치면 안 된다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tree]);

    const toggleOpen = (setter, key) => setter(prev => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
    });

    /** codes 를 한꺼번에 켜거나 끈다. 잠긴 코드는 건드리지 않는다. */
    const setCodes = (codes, checked) => {
        const next = new Set(selected);
        for (const c of codes) {
            if (lockOf(c)) continue;
            checked ? next.add(c) : next.delete(c);
        }
        onChange(next);
    };

    /** 묶음(도메인·탭)의 3-state 계산 — 잠긴 코드는 분모에서 빼야 "전체 선택"이 영원히 안 차는 걸 막는다. */
    const stateOf = (codes) => {
        const on = codes.filter(c => selected?.has(c)).length;
        const editable = codes.filter(c => !lockOf(c));
        const editableOn = editable.filter(c => selected?.has(c)).length;
        return {
            on,
            total: codes.length,
            all: editable.length > 0 && editableOn === editable.length,
            some: on > 0,
            allLocked: editable.length === 0,
        };
    };

    if (tree.length === 0) {
        return <Typography sx={{ fontSize: 12, color: "#94a3b8", py: 1 }}>{emptyText}</Typography>;
    }

    /** 세부 권한 한 줄 (leaf). 계층은 연결선이, 체크박스는 이름 바로 옆에. */
    const PermRow = ({ p }) => {
        const reason = lockOf(p.code);
        const isChecked = !!selected?.has(p.code);
        const row = (
            <Box
                onClick={() => !reason && setCodes([p.code], !isChecked)}
                sx={{
                    pr: ROW_PR, py: 0.5, borderRadius: 1,
                    cursor: reason ? "not-allowed" : "pointer",
                    opacity: reason ? 0.6 : 1,
                    "&:hover": { bgcolor: reason ? "transparent" : "#f8fafc" },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b", lineHeight: 1.35 }}>
                        {p.nameKr}
                    </Typography>
                    {reason && <LockOutlinedIcon sx={{ fontSize: 13, color: "#94a3b8", flexShrink: 0 }} />}
                    <Checkbox
                        size="small"
                        checked={isChecked}
                        disabled={!!reason}
                        onChange={() => setCodes([p.code], !isChecked)}
                        onClick={(e) => e.stopPropagation()}
                        sx={checkboxSx}
                    />
                </Box>
                {p.description && (
                    <Typography sx={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.4, mt: 0.15 }}>
                        {p.description}
                    </Typography>
                )}
            </Box>
        );
        return reason ? <Tooltip title={reason} placement="top-start" arrow>{row}</Tooltip> : row;
    };

    return (
        <Box>
            {tree.map((d, di) => {
                const domainCodes = d.tabs.flatMap(t => t.perms.map(p => p.code));
                const ds = stateOf(domainCodes);
                const dOpen = openDomains.has(d.domain);

                return (
                    <Box key={d.domain}>
                        {di > 0 && <Divider />}

                        {/* ── 도메인 행 ── */}
                        <Box
                            onClick={() => toggleOpen(setOpenDomains, d.domain)}
                            sx={{
                                display: "flex", alignItems: "center", gap: 0.75,
                                pl: DOMAIN_ROW_PL, pr: ROW_PR, py: 0.9,
                                cursor: "pointer", userSelect: "none",
                                "&:hover": { bgcolor: "#f8fafc" },
                            }}
                        >
                            <Typography sx={{
                                fontSize: 12.5, fontWeight: 800, color: "#0f172a",
                                letterSpacing: 0.2, minWidth: 0,
                            }}>
                                {d.domain}
                            </Typography>
                            {/* 묶음 전체가 잠겼으면 여기서 알려준다 — 펼쳐보지 않아도 왜 체크가 안 되는지 알 수 있게 */}
                            {ds.allLocked && <LockedHint />}
                            <Checkbox
                                size="small"
                                checked={ds.all}
                                indeterminate={!ds.all && ds.some}
                                disabled={ds.allLocked}
                                onChange={() => setCodes(domainCodes, !ds.all)}
                                onClick={(e) => e.stopPropagation()}
                                sx={checkboxSx}
                            />
                            <Box sx={{ flex: 1 }} />
                            <Count on={ds.on} total={ds.total} />
                            <Expander open={dOpen} />
                        </Box>

                        {/* ── 탭 목록 ── */}
                        <Collapse in={dOpen} unmountOnExit>
                            <Box sx={{ pb: 0.75 }}>
                                <SubTreeLine inset={TREE_INSET}>
                                {d.tabs.map((t) => {
                                    const tabCodes = t.perms.map(p => p.code);
                                    const ts = stateOf(tabCodes);
                                    const key = `${d.domain}/${t.tab}`;
                                    const tOpen = openTabs.has(key);

                                    // 권한이 1개인 탭은 계층을 접어 권한 행으로 바로 렌더 (탭과 같은 단)
                                    if (t.perms.length === 1) {
                                        return <PermRow key={key} p={t.perms[0]} />;
                                    }

                                    return (
                                        <Box key={key}>
                                            <Box
                                                onClick={() => toggleOpen(setOpenTabs, key)}
                                                sx={{
                                                    display: "flex", alignItems: "center", gap: 0.75,
                                                    pr: ROW_PR, py: 0.6, borderRadius: 1,
                                                    cursor: "pointer", userSelect: "none",
                                                    "&:hover": { bgcolor: "#f8fafc" },
                                                }}
                                            >
                                                <Typography sx={{
                                                    fontSize: 13, fontWeight: 700, color: "#1e293b",
                                                    minWidth: 0,
                                                }}>
                                                    {t.tab}
                                                </Typography>
                                                {ts.allLocked && <LockedHint />}
                                                <Checkbox
                                                    size="small"
                                                    checked={ts.all}
                                                    indeterminate={!ts.all && ts.some}
                                                    disabled={ts.allLocked}
                                                    onChange={() => setCodes(tabCodes, !ts.all)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    sx={checkboxSx}
                                                />
                                                <Box sx={{ flex: 1 }} />
                                                <Count on={ts.on} total={ts.total} />
                                                <Expander open={tOpen} />
                                            </Box>

                                            <Collapse in={tOpen} unmountOnExit>
                                                <Box sx={{ pb: 0.5 }}>
                                                    <SubTreeLine inset={TREE_INSET}>
                                                        {t.perms.map(p => (
                                                            <PermRow key={p.code} p={p} />
                                                        ))}
                                                    </SubTreeLine>
                                                </Box>
                                            </Collapse>
                                        </Box>
                                    );
                                })}
                                </SubTreeLine>
                            </Box>
                        </Collapse>
                    </Box>
                );
            })}
        </Box>
    );
}
